import { JSX, FC, useState, useEffect, Dispatch, SetStateAction } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";
import { LabelInValueType } from "rc-select/lib/Select";
import { ProductsFiltersValuesType, ProductsTagsFiltersType } from "app/types";
import { DefaultOptionType } from "antd/es/select";
import { ProductType } from "products/types";

// utils
import { calcDeclensionByCount, getDeletionWordForm } from "utils/сalcDeclensionByCount";
import { formatFiltersForTags } from "../utils/formatFiltersForTags";
import { parseCategoryFilters } from "../utils/parseCategoryFilters";
import { transformSearchParamsToObject } from "utils/searchParamsToObject";
import { OptionType } from "utils/formatCategories";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";

// components
import SearchFilterPanel from "components/Catalog/SearchFilterPanel/SearchFilterPanel";
import FiltersDrawer from "components/ui/Filters/FiltersDrawer/FiltersDrawer";
import ProductsFilters from "components/Catalog/TableFilters/ProductsFilters/ProductsFilters";
import FiltersDrawerTags from "components/ui/Filters/FiltersDrawerTags/FiltersDrawerTags";
import PrimaryButton from "components/ui/PrimaryButton/PrimaryButton";
import { Form, FormInstance, Select } from "antd";

import css from "components/Catalog/UploadedProductsTab/style.module.css";

interface IUploadedProductsFiltersPanelProps {
  form: FormInstance<ProductsFiltersValuesType>;
  params: URLSearchParams;
  values: ProductsFiltersValuesType;
  selectedRows: ProductType[];
  isLoading: boolean;
  categoriesTree: OptionType[];
  categoriesTPTree: OptionType[];
  setSelectedCategories: Dispatch<SetStateAction<number[]>>;
  setSelectedCategoriesTP: Dispatch<SetStateAction<number[]>>;
  previousFilters: ProductsFiltersValuesType;
  setPreviousFilters: Dispatch<SetStateAction<ProductsFiltersValuesType>>;
  isFiltersConfirmed: boolean;
  isShowDeleteButton: boolean;
  showDeleteModal: (values: ProductType[], text: string) => void;
  setSearchValue: Dispatch<SetStateAction<string>>;
  setIsFiltersConfirmed: Dispatch<SetStateAction<boolean>>;
  countProductsForDelete: number;
  isTagFilterDeleted: boolean;
  setIsTagFilterDeleted: Dispatch<SetStateAction<boolean>>;
}

const statusMapping: Record<string, Record<string, boolean>> = {
  "Размещен": { published: true },
  "Ошибка": { error: true },
  "Черновик": { draft: true },
};

const options: DefaultOptionType[] = [
  { label: "Портал Бизнес Коннект", value: 0 },
  { label: "Вендор Менеджмент", value: 1 }
];

const words: string[] = ["товар", "товара", "товаров"];

const UploadedProductsFiltersPanel: FC<IUploadedProductsFiltersPanelProps> = ({
  form,
  params,
  values,
  selectedRows,
  isLoading,
  categoriesTree,
  categoriesTPTree,
  setSelectedCategories,
  setSelectedCategoriesTP,
  previousFilters,
  setPreviousFilters,
  isFiltersConfirmed,
  isShowDeleteButton,
  showDeleteModal,
  setSearchValue,
  setIsFiltersConfirmed,
  countProductsForDelete,
  isTagFilterDeleted,
  setIsTagFilterDeleted,
}): JSX.Element => {
  const { minPrice, maxPrice } = useSelector((state: AppStateType) => state.productsSlice.products);

  const [isShowFiltersDrawer, setIsShowFiltersDrawer] = useState<boolean>(false);
  const [filters, setFilters] = useState<ProductsTagsFiltersType>({});

  useEffect(() => {
    const searchParams: Record<string, any> = transformSearchParamsToObject(params);
    const { price_from, price_to, updated_at_from, updated_at_to } = searchParams || {};

    if (Object.keys(searchParams)?.length) {
      const statuses: Record<string, boolean> = Object.entries(statusMapping).reduce(
        (acc, [status, fieldValue]) =>
          searchParams.status?.includes(status) ? { ...acc, ...fieldValue } : acc,
        {}
      );

      const newFilters: ProductsFiltersValuesType = {
        ...statuses,
        ...(categoriesTPTree ? { categoryBK: getCategoryFilters(categoriesTPTree, "category_bk") } : {}),
        ...(categoriesTree ? { supplierCategory: getCategoryFilters(categoriesTree, "category_supplier") } : {}),
      };

      if (price_from || price_to) {
        const min: number = price_from && Number(price_from) >= minPrice ? Number(price_from) : minPrice;
        const max: number = price_to && Number(price_to) <= maxPrice ? Number(price_to) : maxPrice;

        form.setFieldsValue({
          price: [min, max],
          minPrice: min,
          maxPrice: max,
        });

        newFilters.price = [min, max];
      }

      if (updated_at_from || updated_at_to) {
        const dateFrom: dayjs.Dayjs | null = updated_at_from ? dayjs(updated_at_from) : null;
        const dateTo: dayjs.Dayjs | null = updated_at_to ? dayjs(updated_at_to) : null;

        form.setFieldsValue({ date: [dateFrom, dateTo] });

        newFilters.date = [dateFrom, dateTo];
      }

      setPreviousFilters((prev) => ({ ...prev, ...newFilters }));

      setFilters(formatFiltersForTags(
        { ...previousFilters, ...newFilters },
        minPrice,
        maxPrice,
        newFilters.date
      ));

      form.setFieldsValue(statuses);
    }
  }, [params, categoriesTPTree, categoriesTree, minPrice, maxPrice]);

  useEffect(() => {
    if (!isNil(minPrice) && !isNil(maxPrice) && previousFilters.price.some((el) => isNil(el))) {
      setPreviousFilters((prev) => ({
        ...prev,
        price: [minPrice, maxPrice]
      }));
    }

    const updatePriceFilter = (newPrice: [number, number]): void => {
      setPreviousFilters((prev) => ({
        ...prev,
        price: newPrice
      }));

      form.setFieldsValue({ price: newPrice });
    };

    // Обработка кейса получения нового диапазона цен и выхода примененной цены из диапазона
    if (values?.price) {
      const [min, max] = values.price;
      const newPrice: [number, number] = [
        min < minPrice ? minPrice : min,
        max > maxPrice ? maxPrice : max
      ];

      if (newPrice[0] !== min || newPrice[1] !== max) {
        updatePriceFilter(newPrice);
      }
    }
  }, [minPrice, maxPrice]);

  useEffect(() => {
    if (values && Object.keys(previousFilters)?.length !== Object.keys(values)?.length) {
      setPreviousFilters((prev) => ({ ...prev, ...values }));
    }
  }, [values]);

  useEffect(() => {
    if (isFiltersConfirmed) {
      !isTagFilterDeleted && setPreviousFilters(values);

      setFilters(formatFiltersForTags(
        isTagFilterDeleted ? previousFilters : values,
        minPrice,
        maxPrice,
        values.date
      ));
    }
  }, [isFiltersConfirmed]);

  const getCategoryFilters = (
    tree: OptionType[],
    searchKey: string,
  ): LabelInValueType[] => parseCategoryFilters(tree, searchKey, params);

  const disabledSubmit: boolean = !Object.keys(values ?? {})?.length || isEqual(previousFilters ?? {}, values ?? {});
  const disabledClearAll: boolean = Object.entries(previousFilters).every(([key, value]) => {
    if (key === "price") {
      return value[0] === minPrice && value[1] === maxPrice;
    }

    return !value || (Array.isArray(value) && !value.length);
  });

  const handleOnSearch = ({ search }): void => setSearchValue(search);

  const handleOnShowFiltersDrawer = (): void => setIsShowFiltersDrawer(true);

  const resetFilters = (): void => {
    form.resetFields();
    setFilters({});
  };

  const confirmText: string = `Будет ${getDeletionWordForm(countProductsForDelete, ["удален", "удалено"])} 
  ${countProductsForDelete} ${calcDeclensionByCount(countProductsForDelete, words)} без возможности восстановления`;

  return (
    <>
      <div className="flex">
        {isShowDeleteButton && (
          <PrimaryButton
            className="mr-3"
            isDisabled={isLoading}
            text={`Удалить (${countProductsForDelete})`}
            onClickHandler={() => showDeleteModal(selectedRows, confirmText)}
          />
        )}
        <Select
          size="large"
          options={options}
          className={`mr-3 ${css.select}`}
          defaultValue={options[0].value}
          disabled={true} // TODO: пока блокируем выбор каталога
        />
        <SearchFilterPanel
          placeholder="Найти по наименованию товара"
          onSearch={handleOnSearch}
          onShowFiltersDrawer={handleOnShowFiltersDrawer}
        />
        <FiltersDrawer
          form={form}
          isOpen={isShowFiltersDrawer}
          disabledSubmit={disabledSubmit}
          disabledClearAll={disabledClearAll}
          resetFilters={resetFilters}
          setIsFiltersOpen={setIsShowFiltersDrawer}
          setIsFiltersConfirmed={setIsFiltersConfirmed}
          setIsTagFilterDeleted={setIsTagFilterDeleted}
        >
          <Form form={form}>
            <ProductsFilters
              form={form}
              values={values}
              params={params}
              categoriesTree={categoriesTree}
              categoriesTPTree={categoriesTPTree}
              setSelectedCategories={setSelectedCategories}
              setSelectedCategoriesTP={setSelectedCategoriesTP}
            />
          </Form>
        </FiltersDrawer>
      </div>
      <FiltersDrawerTags
        form={form}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        setIsFiltersConfirmed={setIsFiltersConfirmed}
        defaultValues={{ price: [minPrice, maxPrice] }}
        previousFilters={previousFilters}
        setPreviousFilters={setPreviousFilters}
        setIsTagFilterDeleted={setIsTagFilterDeleted}
      />
    </>
  );
};

export default UploadedProductsFiltersPanel;
